import React, { useMemo, useState } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import {
  Card,
  Tabs,
  Button,
  Table,
  Tag,
  Space,
  Typography,
  Select,
  Alert,
  message,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  StopOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { trpc } from '../../../lib/trpc';
import { PageHeader, PageContainer } from '../../../components/common';
import { CreateCouponModal } from './components/CreateCouponModal';
import { CreatePromotionCodesModal } from './components/CreatePromotionCodesModal';
import { AssignCouponToAgencyModal } from './components/AssignCouponToAgencyModal';
import { CreateGlobalCouponModal } from './components/CreateGlobalCouponModal';

const { Text } = Typography;

type CouponData = {
  id: string;
  name: string | null;
  percentOff: number | null;
  amountOff: number | null;
  currency: string | null;
  duration: string;
  durationInMonths: number | null;
  valid: boolean;
  created: number;
  metadata: Record<string, string> | null;
};

type PromotionCodeData = {
  id: string;
  code: string;
  coupon: {
    id: string;
    name: string | null;
    percentOff: number | null;
    amountOff: number | null;
    duration: string;
    durationInMonths: number | null;
  };
  active: boolean;
  timesRedeemed: number;
  maxRedemptions: number | null;
  expiresAt: number | null;
  created: number;
  metadata: Record<string, string> | null;
};

type Assignment = {
  id: string;
  agencyUserId: string;
  agencyName?: string | null;
  agencyCouponPrefix?: string | null;
  stripeCouponId: string;
  stripeCouponName: string | null;
  discountType: string;
  discountValue: string;
  currency: string | null;
  duration: string;
  durationInMonths?: number | null;
  isIssued: boolean;
  issuedAt?: string | null;
  stripePromotionCodeId?: string | null;
  promotionCode?: string | null;
  isActive: boolean;
  createdAt: string;
  createdByName?: string | null;
};

type GlobalCoupon = {
  id: string;
  stripeCouponId: string;
  displayName?: string | null;
  stripeCouponName?: string | null;
  discountType: 'percentage' | 'fixed_amount';
  discountValue: string;
  currency?: string | null;
  duration: 'once' | 'forever' | 'repeating';
  durationInMonths?: number | null;
  issueLimit: 'unlimited' | 'once_per_agency' | 'custom';
  maxIssuesPerAgency?: number | null;
  codeType?: string | null;
  isActive: boolean;
  createdAt: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeStringRecord = (value: unknown): Record<string, string> | null => {
  if (!isRecord(value)) {
    return null;
  }

  return Object.entries(value).reduce<Record<string, string>>((acc, [key, item]) => {
    if (typeof item === 'string') {
      acc[key] = item;
    }
    return acc;
  }, {});
};

const normalizeCouponData = (value: unknown): CouponData | null => {
  if (!isRecord(value) || typeof value.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    name: typeof value.name === 'string' ? value.name : null,
    percentOff: typeof value.percent_off === 'number' ? value.percent_off : null,
    amountOff: typeof value.amount_off === 'number' ? value.amount_off : null,
    currency: typeof value.currency === 'string' ? value.currency : null,
    duration: typeof value.duration === 'string' ? value.duration : 'once',
    durationInMonths: typeof value.duration_in_months === 'number' ? value.duration_in_months : null,
    valid: typeof value.valid === 'boolean' ? value.valid : true,
    created: typeof value.created === 'number' ? value.created : 0,
    metadata: normalizeStringRecord(value.metadata),
  };
};

const normalizePromotionCodeData = (value: unknown): PromotionCodeData | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.code !== 'string') {
    return null;
  }

  const coupon = isRecord(value.coupon) ? value.coupon : null;
  if (!coupon || typeof coupon.id !== 'string') {
    return null;
  }

  return {
    id: value.id,
    code: value.code,
    coupon: {
      id: coupon.id,
      name: typeof coupon.name === 'string' ? coupon.name : null,
      percentOff: typeof coupon.percent_off === 'number' ? coupon.percent_off : null,
      amountOff: typeof coupon.amount_off === 'number' ? coupon.amount_off : null,
      duration: typeof coupon.duration === 'string' ? coupon.duration : 'once',
      durationInMonths: typeof coupon.duration_in_months === 'number' ? coupon.duration_in_months : null,
    },
    active: typeof value.active === 'boolean' ? value.active : false,
    timesRedeemed: typeof value.times_redeemed === 'number' ? value.times_redeemed : 0,
    maxRedemptions: typeof value.max_redemptions === 'number' ? value.max_redemptions : null,
    expiresAt: typeof value.expires_at === 'number' ? value.expires_at : null,
    created: typeof value.created === 'number' ? value.created : 0,
    metadata: normalizeStringRecord(value.metadata),
  };
};

const normalizeGlobalCoupon = (value: unknown): GlobalCoupon | null => {
  if (!isRecord(value) || typeof value.id !== 'string' || typeof value.stripeCouponId !== 'string') {
    return null;
  }

  return {
    id: value.id,
    stripeCouponId: value.stripeCouponId,
    displayName: typeof value.displayName === 'string' ? value.displayName : null,
    stripeCouponName: typeof value.stripeCouponName === 'string' ? value.stripeCouponName : null,
    discountType: value.discountType === 'percentage' ? 'percentage' : 'fixed_amount',
    discountValue: typeof value.discountValue === 'string' ? value.discountValue : '0',
    currency: typeof value.currency === 'string' ? value.currency : null,
    duration: value.duration === 'forever' || value.duration === 'repeating' ? value.duration : 'once',
    durationInMonths: typeof value.durationInMonths === 'number' ? value.durationInMonths : null,
    issueLimit: value.issueLimit === 'once_per_agency' || value.issueLimit === 'custom' ? value.issueLimit : 'unlimited',
    maxIssuesPerAgency: typeof value.maxIssuesPerAgency === 'number' ? value.maxIssuesPerAgency : null,
    codeType: typeof value.codeType === 'string' ? value.codeType : null,
    isActive: typeof value.isActive === 'boolean' ? value.isActive : true,
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : '',
  };
};

export function CouponManagementPage() {
  const [activeTab, setActiveTab] = useState('coupons');
  const [createCouponModalOpen, setCreateCouponModalOpen] = useState(false);
  const [createCodesModalOpen, setCreateCodesModalOpen] = useState(false);
  const [assignCouponModalOpen, setAssignCouponModalOpen] = useState(false);
  const [createGlobalCouponModalOpen, setCreateGlobalCouponModalOpen] = useState(false);
  const [selectedCouponFilter, setSelectedCouponFilter] = useState<string>();
  const [activeFilter, setActiveFilter] = useState<boolean>();

  const [messageApi, contextHolder] = message.useMessage();
  const utils = trpc.useUtils();

  const {
    data: couponsData,
    isLoading: couponsLoading,
    error: couponsError,
  } = trpc.stripeCouponAdmin.listCoupons.useQuery({ limit: 100 });
  const {
    data: promotionCodesData,
    isLoading: promotionCodesLoading,
    error: promotionCodesError,
  } = trpc.stripeCouponAdmin.listPromotionCodes.useQuery(
    {
      couponId: selectedCouponFilter || undefined,
      active: typeof activeFilter === 'boolean' ? activeFilter : undefined,
      limit: 100,
    },
    {
      placeholderData: keepPreviousData,
    }
  );
  const {
    data: assignmentsData,
    isLoading: assignmentsLoading,
    error: assignmentsError,
  } = trpc.agencyCouponAssignmentAdmin.list.useQuery({ limit: 50 });
  const {
    data: globalCouponsData,
    isLoading: globalCouponsLoading,
    error: globalCouponsError,
  } = trpc.globalAgencyCouponAdmin.list.useQuery({ limit: 50 });

  const coupons = useMemo(
    () => (couponsData?.data ?? []).flatMap((coupon) => {
      const normalized = normalizeCouponData(coupon);
      return normalized ? [normalized] : [];
    }),
    [couponsData]
  );
  const promotionCodes = useMemo(
    () => (promotionCodesData?.data ?? []).flatMap((promotionCode) => {
      const normalized = normalizePromotionCodeData(promotionCode);
      return normalized ? [normalized] : [];
    }),
    [promotionCodesData]
  );
  const assignments = (assignmentsData as { assignments?: Assignment[] } | undefined)?.assignments ?? [];
  const globalCoupons = useMemo(
    () => (globalCouponsData?.coupons ?? []).flatMap((coupon) => {
      const normalized = normalizeGlobalCoupon(coupon);
      return normalized ? [normalized] : [];
    }),
    [globalCouponsData]
  );

  const updatePromotionCodeStatusMutation =
    trpc.stripeCouponAdmin.updatePromotionCodeStatus.useMutation({
      onSuccess: (_data, variables) => {
        const input = isRecord(variables) ? variables : null;
        const active = typeof input?.active === 'boolean' ? input.active : false;
        messageApi.success(
          `ご紹介コードを${active ? '有効化' : '停止'}しました`
        );
        utils.stripeCouponAdmin.listPromotionCodes.invalidate();
      },
      onError: (error) => {
        messageApi.error(error.message || 'ご紹介コードの更新に失敗しました');
      },
    });

  const deactivateAssignmentMutation = trpc.agencyCouponAssignmentAdmin.deactivate.useMutation({
    onSuccess: () => {
      messageApi.success('割り当てを無効化しました');
      utils.agencyCouponAssignmentAdmin.list.invalidate();
    },
    onError: (error) => {
      messageApi.error(error.message || '割り当ての無効化に失敗しました');
    },
  });

  const deactivateGlobalCouponMutation = trpc.globalAgencyCouponAdmin.deactivate.useMutation({
    onSuccess: () => {
      messageApi.success('グローバルクーポンを無効化しました');
      utils.globalAgencyCouponAdmin.list.invalidate();
    },
    onError: (error) => {
      messageApi.error(error.message || 'グローバルクーポンの無効化に失敗しました');
    },
  });

  const handleCopyToClipboard = async (value: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        messageApi.success('コードをコピーしました');
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch {
      messageApi.warning('クリップボードにコピーできませんでした');
    }
  };

  const handleDeactivateAssignment = (id: string) => {
    deactivateAssignmentMutation.mutate({ assignmentId: id });
  };

  const handleDeactivateGlobalCoupon = (id: string) => {
    deactivateGlobalCouponMutation.mutate({ id });
  };

  const couponColumns: ColumnsType<CouponData> = [
    {
      title: 'クーポン名',
      key: 'name',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name || record.id}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.id}
          </Text>
        </Space>
      ),
    },
    {
      title: '割引内容',
      key: 'discount',
      render: (_, record) => {
        const discount = record.percentOff
          ? `${record.percentOff}%割引`
          : record.amountOff
          ? `${(record.amountOff / 100).toLocaleString('ja-JP')} ${record.currency?.toUpperCase() || 'JPY'}引き`
          : '―';

        return <Text>{discount}</Text>;
      },
    },
    {
      title: '期間',
      key: 'duration',
      render: (_, record) => {
        if (record.duration === 'once') return '1回のみ';
        if (record.duration === 'forever') return '無制限';
        return `${record.durationInMonths ?? '-'}ヶ月間`;
      },
    },
    {
      title: '作成日',
      dataIndex: 'created',
      key: 'created',
      render: (created: number) =>
        new Date(created * 1000).toLocaleString('ja-JP', { hour12: false }),
    },
  ];

  const promotionCodeColumns: ColumnsType<PromotionCodeData> = [
    {
      title: 'コード',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <Space size="small">
          <Text code>{code}</Text>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyToClipboard(code)}
          />
        </Space>
      ),
    },
    {
      title: '紐づくクーポン',
      key: 'coupon',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.coupon.name || record.coupon.id}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.coupon.duration === 'once'
              ? '1回のみ'
              : record.coupon.duration === 'forever'
              ? '無制限'
              : `${record.coupon.durationInMonths ?? '-'}ヶ月`}
          </Text>
        </Space>
      ),
    },
    {
      title: '状態',
      dataIndex: 'active',
      key: 'active',
      render: (active: boolean) =>
        active ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            有効
          </Tag>
        ) : (
          <Tag color="default" icon={<StopOutlined />}>
            停止
          </Tag>
        ),
    },
    {
      title: '利用状況',
      key: 'usage',
      render: (_, record) => (
        <Text>
          {record.timesRedeemed} / {record.maxRedemptions ?? '∞'}回
        </Text>
      ),
    },
    {
      title: '作成日',
      dataIndex: 'created',
      key: 'created',
      render: (created: number) =>
        new Date(created * 1000).toLocaleString('ja-JP', { hour12: false }),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          onClick={() =>
            updatePromotionCodeStatusMutation.mutate({
              promotionCodeId: record.id,
              active: !record.active,
            })
          }
          loading={updatePromotionCodeStatusMutation.isPending}
        >
          {record.active ? '停止する' : '有効化'}
        </Button>
      ),
    },
  ];

  const assignmentColumns: ColumnsType<Assignment> = [
    {
      title: '代理店',
      key: 'agency',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.agencyName || '-'}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.agencyCouponPrefix ? `PREFIX: ${record.agencyCouponPrefix}` : '-'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'クーポン',
      key: 'coupon',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.stripeCouponName || record.stripeCouponId}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.stripeCouponId}
          </Text>
        </Space>
      ),
    },
    {
      title: '状態',
      key: 'status',
      render: (_, record) => (
        <Space size="small">
          <Tag color={record.isActive ? 'success' : 'default'}>
            {record.isActive ? '有効' : '停止'}
          </Tag>
          <Tag color={record.isIssued ? 'blue' : 'default'}>
            {record.isIssued ? '発行済' : '未発行'}
          </Tag>
        </Space>
      ),
    },
    {
      title: '発行済コード',
      dataIndex: 'promotionCode',
      key: 'promotionCode',
      render: (promotionCode: string | null | undefined) =>
        promotionCode ? (
          <Space size="small">
            <Text code>{promotionCode}</Text>
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => handleCopyToClipboard(promotionCode)}
            />
          </Space>
        ) : (
          <Text type="secondary">未発行</Text>
        ),
    },
    {
      title: '作成日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) =>
        new Date(createdAt).toLocaleString('ja-JP', { hour12: false }),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) =>
        record.isActive && !record.isIssued ? (
          <Popconfirm
            title="この割り当てを無効化しますか？"
            description="無効化すると、代理店はこのStripeクーポンから新規コードを発行できなくなります。"
            onConfirm={() => handleDeactivateAssignment(record.id)}
            okText="無効化"
            cancelText="キャンセル"
          >
            <Button
              type="link"
              size="small"
              danger
              loading={deactivateAssignmentMutation.isPending}
            >
              無効化
            </Button>
          </Popconfirm>
        ) : (
          <Text type="secondary">―</Text>
        ),
    },
  ];

  const globalCouponColumns: ColumnsType<GlobalCoupon> = [
    {
      title: 'Stripe クーポンID',
      dataIndex: 'stripeCouponId',
      key: 'stripeCouponId',
      width: 150,
      render: (id: string) => (
        <Text code style={{ fontSize: '12px' }}>
          {id}
        </Text>
      ),
    },
    {
      title: 'クーポン名',
      key: 'couponName',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.displayName || record.stripeCouponName || '-'}</Text>
          {record.displayName && record.stripeCouponName && (
            <Text type="secondary" style={{ fontSize: '11px' }}>
              Stripe: {record.stripeCouponName}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'コードタイプ',
      dataIndex: 'codeType',
      key: 'codeType',
      width: 100,
      render: (codeType: string | null) => <Tag color="blue">{codeType || 'GEN'}</Tag>,
    },
    {
      title: '割引内容',
      key: 'discount',
      width: 150,
      render: (_, record) => {
        const discount =
          record.discountType === 'percentage'
            ? `${record.discountValue}%割引`
            : `¥${Math.floor(Number(record.discountValue)).toLocaleString()}引き`;

        const durationText =
          record.duration === 'once'
            ? '1回のみ'
            : record.duration === 'forever'
            ? '無制限'
            : `${record.durationInMonths ?? '-'}ヶ月`;

        return (
          <Space direction="vertical" size={0}>
            <Text>{discount}</Text>
            <Text type="secondary" style={{ fontSize: '11px' }}>
              {durationText}
            </Text>
          </Space>
        );
      },
    },
    {
      title: '発行制限',
      dataIndex: 'issueLimit',
      key: 'issueLimit',
      width: 150,
      render: (issueLimit: string, record) => {
        if (issueLimit === 'unlimited') {
          return <Tag color="green">無制限</Tag>;
        }
        if (issueLimit === 'once_per_agency') {
          return <Tag color="blue">代理店ごとに1回</Tag>;
        }
        if (issueLimit === 'custom') {
          return <Tag color="orange">カスタム ({record.maxIssuesPerAgency}回)</Tag>;
        }
        return <Text>-</Text>;
      },
    },
    {
      title: '状態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (isActive: boolean) =>
        isActive ? (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            有効
          </Tag>
        ) : (
          <Tag color="default" icon={<StopOutlined />}>
            停止
          </Tag>
        ),
    },
    {
      title: '作成日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (createdAt: string) =>
        new Date(createdAt).toLocaleDateString('ja-JP', { hour12: false }),
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) =>
        record.isActive ? (
          <Popconfirm
            title="このグローバルクーポンを無効化しますか？"
            description="無効化すると、代理店は新たにプロモーションコードを発行できません。"
            onConfirm={() => handleDeactivateGlobalCoupon(record.id)}
            okText="無効化"
            cancelText="キャンセル"
          >
            <Button
              type="link"
              size="small"
              danger
              loading={deactivateGlobalCouponMutation.isPending}
            >
              無効化
            </Button>
          </Popconfirm>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  const tabItems = [
    {
      key: 'coupons',
      label: 'クーポン一覧',
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {couponsError && (
            <Alert
              message="クーポン一覧の取得に失敗しました"
              description={couponsError.message}
              type="error"
              showIcon
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Space>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateCouponModalOpen(true)}
              >
                新規クーポン作成
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => utils.stripeCouponAdmin.listCoupons.invalidate()}
              >
                更新
              </Button>
            </Space>
          </div>

          <Table
            columns={couponColumns}
            dataSource={coupons}
            rowKey="id"
            loading={couponsLoading}
            pagination={{ pageSize: 20 }}
          />
        </Space>
      ),
    },
    {
      key: 'codes',
      label: 'ご紹介コード一覧',
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {promotionCodesError && (
            <Alert
              message="ご紹介コードの取得に失敗しました"
              description={promotionCodesError.message}
              type="error"
              showIcon
            />
          )}

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 16,
            }}
          >
            <Space wrap>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateCodesModalOpen(true)}
              >
                コード一括作成
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => utils.stripeCouponAdmin.listPromotionCodes.invalidate()}
              >
                更新
              </Button>
            </Space>

            <Space wrap>
              <Select
                placeholder="クーポンでフィルター"
                style={{ width: 250 }}
                allowClear
                value={selectedCouponFilter}
                onChange={(value) => setSelectedCouponFilter(value)}
              >
                {coupons.map((coupon) => (
                  <Select.Option key={coupon.id} value={coupon.id}>
                    {coupon.name || coupon.id}
                  </Select.Option>
                ))}
              </Select>

              <Select
                placeholder="状態"
                style={{ width: 120 }}
                allowClear
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as boolean | undefined)}
              >
                <Select.Option value={true}>有効のみ</Select.Option>
                <Select.Option value={false}>停止のみ</Select.Option>
              </Select>
            </Space>
          </div>

          <Table
            columns={promotionCodeColumns}
            dataSource={promotionCodes}
            rowKey="id"
            loading={promotionCodesLoading}
            pagination={{ pageSize: 50 }}
          />
        </Space>
      ),
    },
    {
      key: 'assignments',
      label: '代理店への割り当て',
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {assignmentsError && (
            <Alert
              message="割り当て一覧の取得に失敗しました"
              description={assignmentsError.message}
              type="error"
              showIcon
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setAssignCouponModalOpen(true)}
            >
              クーポンを代理店に割り当て
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => utils.agencyCouponAssignmentAdmin.list.invalidate()}
            >
              更新
            </Button>
          </div>

          <Table
            columns={assignmentColumns}
            dataSource={assignments}
            rowKey="id"
            loading={assignmentsLoading}
            pagination={{ pageSize: 20 }}
          />
        </Space>
      ),
    },
    {
      key: 'globalCoupons',
      label: 'グローバルクーポン',
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            message="グローバルクーポンについて"
            description="グローバルクーポンは全ての代理店に対して利用可能なクーポンです。代理店ごとに発行上限を設定でき、新規代理店向けキャンペーンなどを一括管理できます。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {globalCouponsError && (
            <Alert
              message="グローバルクーポンの取得に失敗しました"
              description={globalCouponsError.message}
              type="error"
              showIcon
            />
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateGlobalCouponModalOpen(true)}
            >
              グローバルクーポン作成
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => utils.globalAgencyCouponAdmin.list.invalidate()}
            >
              更新
            </Button>
          </div>

          <Table
            columns={globalCouponColumns}
            dataSource={globalCoupons}
            rowKey="id"
            loading={globalCouponsLoading}
            pagination={{ pageSize: 20 }}
          />
        </Space>
      ),
    },
  ];

  return (
    <PageContainer>
      {contextHolder}

      <PageHeader
        title="クーポン・ご紹介コード管理"
        subtitle="Stripeのクーポンとご紹介コードを一元管理できます。クーポンの作成や代理店ごとの割り当て、グローバルクーポンの制御までこの画面から操作してください。"
      />

        <Card>
          <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
        </Card>

        <CreateCouponModal
          open={createCouponModalOpen}
          onClose={() => setCreateCouponModalOpen(false)}
          onSuccess={() => {
            setCreateCouponModalOpen(false);
            utils.stripeCouponAdmin.listCoupons.invalidate();
            messageApi.success('クーポンを作成しました');
          }}
        />

        <CreatePromotionCodesModal
          open={createCodesModalOpen}
          coupons={coupons}
          onClose={() => setCreateCodesModalOpen(false)}
          onSuccess={(result) => {
            setCreateCodesModalOpen(false);
            utils.stripeCouponAdmin.listPromotionCodes.invalidate();

            if (result.errors.length === 0) {
              messageApi.success(`${result.created.length}件のコードを作成しました`);
            } else {
              messageApi.warning(
                `${result.created.length}件成功 / ${result.errors.length}件失敗しました`
              );
            }
          }}
        />

        <AssignCouponToAgencyModal
          open={assignCouponModalOpen}
          onCancel={() => setAssignCouponModalOpen(false)}
          onSuccess={() => {
            setAssignCouponModalOpen(false);
            utils.agencyCouponAssignmentAdmin.list.invalidate();
          }}
        />

        <CreateGlobalCouponModal
          open={createGlobalCouponModalOpen}
          onClose={() => setCreateGlobalCouponModalOpen(false)}
          onSuccess={() => {
            setCreateGlobalCouponModalOpen(false);
            utils.globalAgencyCouponAdmin.list.invalidate();
            messageApi.success('グローバルクーポンを作成しました');
          }}
        />
    </PageContainer>
  );
}

export default CouponManagementPage;
